import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Sparkles,
  TrendingUp,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown,
  Lock,
  PieChart,
  CreditCard,
  Target,
  Shield,
  ListOrdered,
  DollarSign
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import AIDisclaimer from './AIDisclaimer';
import EnhancedAIAnalysis from './EnhancedAIAnalysis';
import { sanitizePortfolioForGemini, validateNoPII } from '../utils/geminiDataSanitizer';
import { useAuth } from '../AuthContext';

/**
 * Feature Card Component for "What's Included" section
 */
const AnalysisFeatureCard = ({ icon: Icon, label, available, badge, description }) => (
  <div className={`relative p-4 rounded-lg border-2 transition-all ${
    available
      ? 'border-green-200 bg-green-50'
      : 'border-gray-200 bg-gray-50 opacity-60'
  }`}>
    {badge && (
      <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
        {badge}
      </span>
    )}
    <div className="flex items-start gap-3">
      <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
        available ? 'text-green-600' : 'text-gray-400'
      }`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={`text-sm font-semibold ${
            available ? 'text-gray-900' : 'text-gray-500'
          }`}>
            {label}
          </h4>
          {available && <Check className="h-4 w-4 text-green-600 flex-shrink-0" />}
        </div>
        {description && (
          <p className="text-xs text-gray-600 mt-1">{description}</p>
        )}
      </div>
    </div>
  </div>
);

/**
 * Health Metric Card Component for Dashboard Summary
 */
const HealthMetricCard = ({ icon: Icon, label, value, status, trend, iconColor, bgColor, onClick }) => {
  const statusColors = {
    excellent: 'text-green-600',
    good: 'text-green-600',
    adequate: 'text-blue-600',
    moderate: 'text-yellow-600',
    warning: 'text-orange-600',
    critical: 'text-red-600',
    positive: 'text-green-600',
    negative: 'text-red-600',
    neutral: 'text-gray-600'
  };

  return (
    <div
      className={`${bgColor} rounded-xl p-4 transition-all hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg ${iconColor} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend && (
          <span className={`text-xs font-semibold ${statusColors[status] || 'text-gray-600'}`}>
            {trend}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {status && (
        <p className={`text-xs font-medium mt-1 ${statusColors[status]}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </p>
      )}
    </div>
  );
};

/**
 * Extract section headings from markdown for jump links
 */
const extractSections = (markdown) => {
  if (!markdown) return [];
  const sections = [];
  const lines = markdown.split('\n');

  lines.forEach((line, index) => {
    // Match ## headings (h2 level - main sections)
    const match = line.match(/^##\s+(.+)$/);
    if (match) {
      const title = match[1].trim();
      // Create anchor-friendly ID
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      sections.push({ title, id, level: 2 });
    }
  });

  return sections;
};

/**
 * AI Financial Health Check Component
 * Provides comprehensive financial analysis including:
 * - Portfolio analysis (asset allocation, sectors, holdings)
 * - Net worth breakdown (assets, liabilities, debt ratios)
 * - Debt payoff strategies (avalanche vs snowball)
 * - Goal-based allocation (bucket strategies, glide paths)
 * - Emergency fund analysis
 * - Financial priority waterfall (step-by-step action plan)
 *
 * Two analysis modes:
 * 1. Built-in Rule-Based Analysis (quantitative, metrics-focused)
 * 2. Gemini AI Educational Analysis (qualitative, concept-focused)
 *
 * Premium-only feature with usage limits (5/month)
 */
const AIInsights = ({
  investments = [],
  accounts = [],
  cryptocurrencies = [],
  portfolioData = [],
  retirementGoals = {},
  aiAnalysis = null,
  analyzing = false,
  runAIAnalysis = null,
  onUpgrade = null,
  selectedCountry = 'US'
}) => {
  const { userProfile } = useAuth();

  // State management
  const [analysisType, setAnalysisType] = useState('builtin'); // 'builtin' or 'gemini'
  const [geminiAnalysis, setGeminiAnalysis] = useState(null);
  const [sanitizedDataCache, setSanitizedDataCache] = useState(null); // Store sanitized data for dashboard
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [usageInfo, setUsageInfo] = useState({ used: 0, limit: 5 });
  const [analysisTimestamp, setAnalysisTimestamp] = useState(null); // Track when analysis was generated

  // Check if user has premium access (includes admin users)
  const isPremium = userProfile?.plan === 'premium';
  const isAdmin = userProfile?.isAdmin || false;

  // Load cached analysis from localStorage on mount
  useEffect(() => {
    if (!userProfile?.uid) return;

    const cacheKey = `gemini_analysis_${userProfile.uid}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const { analysis, sanitizedData, timestamp } = JSON.parse(cached);
        const ageInHours = (Date.now() - timestamp) / (1000 * 60 * 60);

        // Load cache if less than 24 hours old
        if (ageInHours < 24) {
          console.log('[AI Insights] Loading cached analysis from', new Date(timestamp).toLocaleString());
          setGeminiAnalysis(analysis);
          setSanitizedDataCache(sanitizedData);
          setAnalysisTimestamp(timestamp);
        } else {
          console.log('[AI Insights] Cached analysis expired, clearing...');
          localStorage.removeItem(cacheKey);
        }
      } catch (err) {
        console.error('[AI Insights] Failed to load cached analysis:', err);
        localStorage.removeItem(cacheKey);
      }
    }
  }, [userProfile?.uid]);

  // Reset error when switching analysis types
  useEffect(() => {
    setError(null);
  }, [analysisType]);

  /**
   * Generate Gemini AI Analysis
   */
  const generateGeminiAnalysis = async () => {
    if (!isPremium) {
      setError('This feature requires a premium subscription. Please upgrade to access AI Educational Analysis.');
      return;
    }

    setLoading(true);
    setError(null);
    setGeminiAnalysis(null);

    try {
      console.log('[AI Insights] Preparing comprehensive financial data for Gemini...');

      // Fetch liabilities from Firestore
      let liabilities = [];
      try {
        const liabilitiesQuery = query(
          collection(db, 'users', userProfile.uid, 'liabilities')
        );
        const liabilitiesSnapshot = await getDocs(liabilitiesQuery);
        liabilities = liabilitiesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`[AI Insights] Loaded ${liabilities.length} liabilities`);
      } catch (liabErr) {
        console.warn('[AI Insights] Could not load liabilities:', liabErr);
        // Continue without liabilities if fetch fails
      }

      // Fetch financial goals from Firestore
      let financialGoals = [];
      try {
        const goalsQuery = query(
          collection(db, 'users', userProfile.uid, 'financialGoals')
        );
        const goalsSnapshot = await getDocs(goalsQuery);
        financialGoals = goalsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`[AI Insights] Loaded ${financialGoals.length} financial goals`);
      } catch (goalsErr) {
        console.warn('[AI Insights] Could not load financial goals:', goalsErr);
        // Continue without goals if fetch fails
      }

      // Get cash reserves from Emergency Fund goal if it exists, otherwise from retirement goals
      const emergencyFundGoal = financialGoals.find(g => g.type === 'Emergency Fund');
      const cashReserves = emergencyFundGoal
        ? parseFloat(emergencyFundGoal.currentAmount) || 0
        : parseFloat(retirementGoals.currentSavings) || 0;

      console.log('[AI Insights] Cash reserves source:', emergencyFundGoal ? 'Emergency Fund goal' : 'Retirement savings');
      console.log('[AI Insights] Cash reserves amount:', cashReserves);

      // Sanitize comprehensive financial data
      const sanitizedData = sanitizePortfolioForGemini(
        investments,
        accounts,
        cryptocurrencies,
        { country: userProfile.country },
        retirementGoals,
        liabilities,
        financialGoals,
        cashReserves
      );

      // Validate no PII
      if (!validateNoPII(sanitizedData)) {
        throw new Error('Data validation failed: PII detected. Please contact support.');
      }

      console.log('[AI Insights] Data sanitized successfully');
      console.log('[AI Insights] - Portfolio summary: ✓');
      console.log(`[AI Insights] - Net worth data: ${sanitizedData.net_worth_summary ? '✓' : '✗'}`);
      console.log(`[AI Insights] - Financial goals: ${sanitizedData.financial_goals?.length || 0} goals`);
      console.log(`[AI Insights] - Debt payoff strategy: ${sanitizedData.debt_payoff_strategy ? '✓' : '✗'}`);
      console.log(`[AI Insights] - Goal-based allocation: ${sanitizedData.goal_based_allocation ? '✓' : '✗'}`);
      console.log(`[AI Insights] - Emergency fund analysis: ${sanitizedData.emergency_fund_analysis ? '✓' : '✗'}`);
      console.log(`[AI Insights] - Financial priority waterfall: ${sanitizedData.financial_priority_waterfall ? '✓' : '✗'}`);

      console.log('[AI Insights] Calling Cloud Function...');

      // Call Firebase Cloud Function with comprehensive data
      const functions = getFunctions();
      const generateAnalysis = httpsCallable(functions, 'generatePortfolioAnalysis');

      const result = await generateAnalysis(sanitizedData);

      console.log('[AI Insights] Analysis received successfully');

      // Update state with results and cache sanitized data for dashboard
      const timestamp = Date.now();
      setGeminiAnalysis(result.data.analysis);
      setSanitizedDataCache(sanitizedData); // Store for dashboard metrics
      setAnalysisTimestamp(timestamp);
      setUsageInfo({
        used: 5 - result.data.remaining,
        limit: 5
      });

      // Save to localStorage for persistence across navigation
      try {
        const cacheKey = `gemini_analysis_${userProfile.uid}`;
        const cacheData = {
          analysis: result.data.analysis,
          sanitizedData: sanitizedData,
          timestamp: timestamp
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log('[AI Insights] Analysis cached to localStorage');
      } catch (err) {
        console.error('[AI Insights] Failed to cache analysis:', err);
        // Non-critical error, continue
      }

    } catch (err) {
      console.error('[AI Insights] Error:', err);
      console.error('[AI Insights] Error code:', err.code);
      console.error('[AI Insights] Error message:', err.message);

      // Handle different error types with more detailed messages
      if (err.code === 'unauthenticated') {
        setError('Please sign in to use this feature.');
      } else if (err.code === 'permission-denied') {
        setError(err.message || 'You need a premium subscription to access this feature.');
      } else if (err.code === 'resource-exhausted') {
        setError(err.message || 'You have reached your monthly limit. Limit resets next month.');
      } else if (err.code === 'unavailable') {
        setError(
          'Unable to connect to AI service. This usually means:\n\n' +
          '• The Gemini API key is not configured in Firebase Functions\n' +
          '• Network connectivity issues\n' +
          '• The API service is temporarily down\n\n' +
          'If you are the app administrator, please check the GEMINI_CONNECTION_FIX.md file for troubleshooting steps.'
        );
      } else if (err.code === 'failed-precondition') {
        setError(
          'AI service is not configured. This usually means the Gemini API key has not been set up. ' +
          'If you are the app administrator, please configure the API key using: ' +
          'firebase functions:config:set gemini.key="YOUR_KEY"'
        );
      } else {
        setError(
          err.message ||
          'An error occurred while generating the analysis. Please try again. ' +
          'If the problem persists, please contact support.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copy analysis to clipboard
   */
  const copyToClipboard = async () => {
    if (!geminiAnalysis) return;

    try {
      await navigator.clipboard.writeText(geminiAnalysis);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[AI Insights] Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Sparkles className="h-7 w-7" />
              AI Financial Health Check
            </h2>
            <p className="text-indigo-100">
              Comprehensive analysis of your portfolio, net worth, debt, goals, and financial priorities
            </p>
          </div>
          {isPremium && (
            <div className="bg-white/20 rounded-lg px-4 py-2 backdrop-blur-sm">
              <div className="text-sm font-medium">Premium Feature</div>
              <div className="text-xs text-indigo-100">
                {usageInfo.used}/{usageInfo.limit} analyses used this month
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Type Selector */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Analysis Type
        </label>
        <div className="relative">
          <select
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            className="block w-full px-4 py-3 pr-10 text-base border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg appearance-none bg-white cursor-pointer transition-colors"
          >
            <option value="builtin">
              Built-in Analysis - Quantitative Metrics & Risk Assessment
            </option>
            <option value="gemini">
              Gemini AI Analysis - Educational Concepts & Explanations {!isPremium ? '(Premium Only)' : ''}
            </option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        </div>

        {/* Description of selected analysis type */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          {analysisType === 'builtin' ? (
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Built-in Analysis</h4>
                <p className="text-sm text-gray-600">
                  Comprehensive quantitative analysis including Sharpe ratios, diversification scores,
                  Monte Carlo simulations, historical backtesting, and risk-adjusted metrics.
                  Best for data-driven decision support.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  AI Financial Health Check
                  {!isPremium && <Lock className="h-4 w-4 text-amber-600" />}
                </h4>
                <p className="text-sm text-gray-600">
                  Comprehensive AI-powered analysis covering your complete financial picture: portfolio allocation,
                  net worth breakdown, debt payoff strategies (avalanche vs snowball), goal-based investing,
                  emergency fund adequacy, and a personalized financial priority action plan. Educational insights
                  explain complex concepts in simple terms using real examples and historical data.
                  {!isPremium && (
                    <span className="block mt-2 text-amber-700 font-medium">
                      Requires premium subscription. 5 analyses per month.
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Display Area */}
      {analysisType === 'builtin' ? (
        /* Built-in Analysis Component */
        <EnhancedAIAnalysis
          aiAnalysis={aiAnalysis}
          analyzing={analyzing}
          runAIAnalysis={runAIAnalysis}
          userProfile={userProfile}
          onUpgrade={onUpgrade}
          selectedCountry={selectedCountry}
        />
      ) : (
        /* Gemini AI Analysis Section */
        <div className="space-y-6">
          {/* Disclaimer - Always shown first */}
          <AIDisclaimer />

          {/* What's Included Preview */}
          {!geminiAnalysis && !loading && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Your Comprehensive Health Check Will Include:
                </h3>
                <p className="text-sm text-gray-600">
                  We'll analyze all available data to provide personalized insights
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <AnalysisFeatureCard
                  icon={PieChart}
                  label="Portfolio Analysis"
                  available={investments.length > 0 || cryptocurrencies.length > 0}
                  description="Asset allocation, sectors, geographic exposure, top holdings"
                />

                <AnalysisFeatureCard
                  icon={DollarSign}
                  label="Net Worth Breakdown"
                  available={true}
                  description="Assets, liabilities, debt ratios, financial health indicators"
                />

                <AnalysisFeatureCard
                  icon={CreditCard}
                  label="Debt Payoff Strategy"
                  available={true}
                  description="Avalanche vs Snowball comparison with UK/US specific guidance"
                />

                <AnalysisFeatureCard
                  icon={Target}
                  label="Goal-Based Allocation"
                  available={true}
                  description="Bucket strategy, time-horizon recommendations, glide paths"
                />

                <AnalysisFeatureCard
                  icon={Shield}
                  label="Emergency Fund Analysis"
                  available={true}
                  description="Coverage assessment, adequacy recommendations"
                />

                <AnalysisFeatureCard
                  icon={ListOrdered}
                  label="Financial Priority Plan"
                  available={true}
                  badge="NEW"
                  description="Step-by-step action plan based on industry best practices"
                />
              </div>

              <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r">
                <p className="text-sm text-blue-800">
                  <strong>Smart Analysis:</strong> Sections only appear if you have relevant data.
                  For example, debt strategies appear only if you have liabilities,
                  and goal allocation appears only if you've set financial goals.
                </p>
              </div>
            </div>
          )}

          {/* Generate Button */}
          {!geminiAnalysis && !loading && (
            <div className="text-center py-8">
              <button
                onClick={generateGeminiAnalysis}
                disabled={!isPremium || loading}
                className={`px-8 py-4 rounded-lg font-semibold text-white shadow-lg transform transition-all duration-200 flex items-center gap-3 mx-auto ${
                  !isPremium
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:scale-105 active:scale-95'
                }`}
              >
                <Sparkles className="h-5 w-5" />
                Generate Financial Health Check
                {!isPremium && <Lock className="h-5 w-5" />}
              </button>
              {!isPremium && (
                <p className="mt-4 text-sm text-gray-600">
                  Upgrade to premium to access comprehensive AI-powered financial analysis
                </p>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Analyzing Your Complete Financial Picture...
              </h3>
              <p className="text-gray-600">
                Our AI is analyzing your portfolio, net worth, debt, goals, emergency fund, and creating
                a personalized financial priority plan. This may take 10-30 seconds.
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
              <div className="flex items-start">
                <AlertCircle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800 mb-1">
                    Analysis Generation Failed
                  </h3>
                  <div className="text-sm text-red-700 whitespace-pre-line">{error}</div>
                  <button
                    onClick={generateGeminiAnalysis}
                    disabled={!isPremium}
                    className="mt-3 text-sm font-medium text-red-800 hover:text-red-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {geminiAnalysis && !loading && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Header with Copy and Regenerate Buttons */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      Your Financial Health Check Results
                    </h3>
                    {analysisTimestamp && (
                      <p className="text-xs text-gray-600 mt-1">
                        Generated {new Date(analysisTimestamp).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={generateGeminiAnalysis}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                      title="Generate fresh analysis with latest data"
                    >
                      <Sparkles className="h-4 w-4" />
                      Regenerate
                    </button>
                    <button
                      onClick={copyToClipboard}
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Financial Health Dashboard - Phase 2 Enhancement */}
              {sanitizedDataCache && (
                <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-blue-50 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Financial Health Overview
                  </h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {/* Net Worth Metric */}
                    {sanitizedDataCache.net_worth_summary && (
                      <HealthMetricCard
                        icon={DollarSign}
                        label="Net Worth"
                        value={sanitizedDataCache.net_worth_summary.net_worth_range}
                        status={sanitizedDataCache.net_worth_summary.is_positive ? 'positive' : 'negative'}
                        iconColor="bg-blue-600"
                        bgColor="bg-white"
                      />
                    )}

                    {/* Debt-to-Asset Ratio */}
                    {sanitizedDataCache.net_worth_summary?.debt_to_asset_ratio !== undefined && (
                      <HealthMetricCard
                        icon={CreditCard}
                        label="Debt Ratio"
                        value={`${sanitizedDataCache.net_worth_summary.debt_to_asset_ratio.toFixed(0)}%`}
                        status={
                          sanitizedDataCache.net_worth_summary.debt_to_asset_ratio < 30 ? 'good' :
                          sanitizedDataCache.net_worth_summary.debt_to_asset_ratio < 50 ? 'moderate' :
                          'warning'
                        }
                        iconColor="bg-purple-600"
                        bgColor="bg-white"
                      />
                    )}

                    {/* Emergency Fund */}
                    {sanitizedDataCache.emergency_fund_analysis && (
                      <HealthMetricCard
                        icon={Shield}
                        label="Emergency Fund"
                        value={`${sanitizedDataCache.emergency_fund_analysis.estimated_months_coverage} months`}
                        status={sanitizedDataCache.emergency_fund_analysis.status}
                        iconColor="bg-green-600"
                        bgColor="bg-white"
                      />
                    )}

                    {/* Goals Progress */}
                    {sanitizedDataCache.financial_goals && sanitizedDataCache.financial_goals.length > 0 && (
                      <HealthMetricCard
                        icon={Target}
                        label="Active Goals"
                        value={`${sanitizedDataCache.financial_goals.length} ${sanitizedDataCache.financial_goals.length === 1 ? 'Goal' : 'Goals'}`}
                        status={
                          sanitizedDataCache.financial_goals.filter(g => g.status === 'On Track' || g.status === 'Completed').length >= sanitizedDataCache.financial_goals.length * 0.7 ? 'good' :
                          sanitizedDataCache.financial_goals.filter(g => g.status === 'At Risk' || g.status === 'Urgent').length > 0 ? 'warning' :
                          'moderate'
                        }
                        iconColor="bg-amber-600"
                        bgColor="bg-white"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Section Jump Links - Phase 2 Enhancement */}
              {(() => {
                const sections = extractSections(geminiAnalysis);
                return sections.length > 0 && (
                  <div className="p-4 bg-white border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Navigation</h4>
                    <div className="flex flex-wrap gap-2">
                      {sections.slice(0, 8).map((section, index) => (
                        <a
                          key={index}
                          href={`#${section.id}`}
                          className="text-xs px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100 transition-colors font-medium"
                          onClick={(e) => {
                            e.preventDefault();
                            const element = document.getElementById(section.id);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                        >
                          {section.title}
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Analysis Content */}
              <div className="p-6 prose prose-indigo max-w-none">
                <ReactMarkdown
                  components={{
                    // Custom styling for markdown elements with IDs for jump links
                    h1: ({ node, ...props }) => (
                      <h1 className="text-3xl font-bold text-gray-900 mt-6 mb-4" {...props} />
                    ),
                    h2: ({ node, children, ...props }) => {
                      // Create ID from heading text for jump links
                      const text = String(children);
                      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                      return (
                        <h2
                          id={id}
                          className="text-2xl font-bold text-gray-800 mt-8 mb-3 pb-2 border-b-2 border-indigo-200 scroll-mt-4"
                          {...props}
                        >
                          {children}
                        </h2>
                      );
                    },
                    h3: ({ node, ...props }) => (
                      <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3" {...props} />
                    ),
                    h4: ({ node, ...props }) => (
                      <h4 className="text-lg font-semibold text-gray-700 mt-4 mb-2" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="text-gray-700 leading-relaxed mb-4" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="list-disc list-outside pl-6 space-y-2 mb-4 text-gray-700" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="list-decimal list-outside pl-6 space-y-2 mb-4 text-gray-700" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="leading-relaxed" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="font-semibold text-gray-900" {...props} />
                    ),
                    code: ({ node, ...props }) => (
                      <code className="bg-gray-100 text-indigo-600 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
                    ),
                    hr: ({ node, ...props }) => (
                      <hr className="my-8 border-gray-300" {...props} />
                    ),
                  }}
                >
                  {geminiAnalysis}
                </ReactMarkdown>
              </div>

              {/* Footer Actions */}
              <div className="bg-gray-50 border-t border-gray-200 p-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Analysis generated on {new Date().toLocaleDateString()} at{' '}
                  {new Date().toLocaleTimeString()}
                </p>
                <button
                  onClick={generateGeminiAnalysis}
                  disabled={loading}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700 underline"
                >
                  Generate New Analysis
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIInsights;
